// src/components/superuser/PortfolioUsers.js
//
// All users for the superuser's client: active accounts + outstanding invites
// (pending / expired / revoked) in one table. Shows role, assigned properties,
// status, last login and last activity, employee linkage, and invite metadata,
// with search + role/status filters and per-invite actions (copy link, revoke).
//
// Users/invites are scoped by the indexed top-level `clientId` field
// (where clientId == selectedClient) — the same field Firestore security rules
// use to keep each client's users isolated. Existing docs are backfilled with
// scripts/backfillUserClientId.js.

import React, { useContext, useMemo, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { firestore } from "../../firebase";
import { clientPropertiesCollection } from "../../utils/firestorePaths";
import PropertyContext from "../../contexts/PropertyContext";
import { formatDistanceToNow, format } from "date-fns";
import UserEditDialog from "./UserEditDialog";
import AddUserDialog from "./AddUserDialog";
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BlockIcon from "@mui/icons-material/Block";
import BadgeIcon from "@mui/icons-material/Badge";
import SendIcon from "@mui/icons-material/Send";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

const ROLE_LABELS = {
  manager: "Property Manager",
  facilities: "Facilities",
  maintenance: "Maintenance",
  housekeeping: "Housekeeping",
  frontDesk: "Front Desk",
  superuser: "Client Superuser",
};
const roleLabel = (t) => ROLE_LABELS[t] || t || "—";

const STATUS_STYLES = {
  active: { label: "Active", color: "#2e7d32" },
  pending: { label: "Pending Invite", color: "#0288d1" },
  expired: { label: "Invite Expired", color: "#ed6c02" },
  revoked: { label: "Revoked", color: "#9e9e9e" },
  suspended: { label: "Suspended", color: "#c62828" },
};

const fmtRelative = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return formatDistanceToNow(d, { addSuffix: true });
};
const fmtAbsolute = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : format(d, "PP p");
};

export default function PortfolioUsers(props) {
  const ctx = useContext(PropertyContext);
  const clientId = ctx.selectedClient || props.selectedClient || props.clientId;

  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [resendingId, setResendingId] = useState(null);
  const [snack, setSnack] = useState(null); // { severity, message }
  const [clientProperties, setClientProperties] = useState([]);
  const [editRow, setEditRow] = useState(null); // row being edited
  const [addOpen, setAddOpen] = useState(false);
  const [sortField, setSortField] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  // Reset portfolio mode if we arrived here drilled into a property.
  React.useEffect(() => {
    if (clientId && ctx.selectedProperty && ctx.selectedProperty !== "all-properties") {
      ctx.setSelectedProperty("all-properties", clientId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.selectedProperty, clientId]);

  // Active user accounts — scoped to this client via the indexed clientId field
  // (the same field security rules use to keep clients' users isolated).
  React.useEffect(() => {
    if (!clientId) return;
    const unsub = onSnapshot(
      query(collection(firestore, "users"), where("clientId", "==", clientId)),
      (snap) => setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("users listener", err)
    );
    return unsub;
  }, [clientId]);

  // Invitations — scoped to this client.
  React.useEffect(() => {
    if (!clientId) return;
    const unsub = onSnapshot(
      query(
        collection(firestore, "userInvites"),
        where("clientId", "==", clientId)
      ),
      (snap) => setInvites(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("invites listener", err)
    );
    return unsub;
  }, [clientId]);

  // This client's properties — powers the assignment selectors in the dialogs.
  React.useEffect(() => {
    if (!clientId) return;
    const unsub = onSnapshot(
      query(clientPropertiesCollection(clientId)),
      (snap) =>
        setClientProperties(
          snap.docs.map((d) => ({
            id: d.id,
            displayName: d.data().displayName || d.data().name || d.id,
          }))
        ),
      (err) => console.error("client properties listener", err)
    );
    return unsub;
  }, [clientId]);

  // Build the unified, client-scoped row set.
  const rows = useMemo(() => {
    const now = Date.now();

    const userRows = users.map((u) => ({
        key: `u_${u.id}`,
        kind: "user",
        id: u.id,
        name: u.fullName || u.userName || u.username || "—",
        email: u.personalEmail || u.email || "—",
        userType: u.userType,
        properties: u.properties || [],
        status: u.suspended ? "suspended" : "active",
        lastLoginAt: u.lastLoginAt || null,
        lastActiveAt: u.lastActiveAt || null,
        createdAt: u.accountCreatedAt || u.createdAt || null,
        employeeLinked: (u.employeeRecords || []).length > 0,
        notifications: !!u.notifications,
      }));

    // Emails with a real account — used to hide superseded invites.
    const activeEmails = new Set(
      userRows.map((r) => (r.email || "").toLowerCase())
    );

    const inviteRows = invites
      .filter((iv) => iv.status !== "completed") // completed → represented by a user row
      .filter((iv) => !activeEmails.has((iv.email || "").toLowerCase()))
      .map((iv) => {
        let status = iv.status || "pending";
        if (status === "pending" && iv.expiresAt && new Date(iv.expiresAt).getTime() < now) {
          status = "expired";
        }
        return {
          key: `i_${iv.id}`,
          kind: "invite",
          id: iv.id,
          name:
            `${iv.firstName || ""} ${iv.lastName || ""}`.trim() ||
            iv.userName ||
            "—",
          email: iv.email || "—",
          userType: iv.userType,
          properties: iv.properties || [],
          status,
          lastLoginAt: null,
          lastActiveAt: null,
          createdAt: iv.invitedAt || null,
          invitedBy: iv.invitedBy,
          invitedAt: iv.invitedAt,
          expiresAt: iv.expiresAt,
          employeeLinked: !!iv.employeeId,
        };
      });

    return [...userRows, ...inviteRows];
  }, [users, invites]);

  // Summary counts (pre-filter).
  const counts = useMemo(() => {
    const c = { total: rows.length, active: 0, pending: 0, expired: 0 };
    rows.forEach((r) => {
      if (r.status === "active") c.active += 1;
      else if (r.status === "pending") c.pending += 1;
      else if (r.status === "expired") c.expired += 1;
    });
    return c;
  }, [rows]);

  const roleOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.userType).filter(Boolean))),
    [rows]
  );

  // Comparable value per sortable column (null = "empty", always sorted last).
  const sortValue = (r, field) => {
    switch (field) {
      case "email":
        return (r.email || "").toLowerCase();
      case "role":
        return roleLabel(r.userType).toLowerCase();
      case "status":
        return (STATUS_STYLES[r.status]?.label || r.status || "").toLowerCase();
      case "lastLogin":
        return r.lastLoginAt ? new Date(r.lastLoginAt).getTime() : null;
      case "lastActive":
        return r.lastActiveAt ? new Date(r.lastActiveAt).getTime() : null;
      case "name":
      default:
        return (r.name || "").toLowerCase();
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => (roleFilter === "all" ? true : r.userType === roleFilter))
      .filter((r) => (statusFilter === "all" ? true : r.status === statusFilter))
      .filter(
        (r) =>
          !q ||
          r.name.toLowerCase().includes(q) ||
          r.email.toLowerCase().includes(q)
      )
      .sort((a, b) => {
        const av = sortValue(a, sortField);
        const bv = sortValue(b, sortField);
        // Empty values always sink to the bottom regardless of direction.
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return sortDir === "asc" ? cmp : -cmp;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, search, roleFilter, statusFilter, sortField, sortDir]);

  const copyInviteLink = (id) => {
    const link = `${window.location.origin}/register?invite=${id}`;
    navigator.clipboard.writeText(link);
    setSnack({ severity: "success", message: "Registration link copied" });
  };

  const revokeInvite = async (id) => {
    try {
      await updateDoc(doc(firestore, "userInvites", id), {
        status: "revoked",
        revokedAt: new Date().toISOString(),
      });
      setSnack({ severity: "info", message: "Invitation revoked" });
    } catch (err) {
      console.error("revoke invite failed", err);
      setSnack({ severity: "error", message: "Could not revoke invitation" });
    }
  };

  // Re-send the invitation email via the resendUserInvite cloud function. This
  // renews the expiry and flips an expired/revoked invite back to pending.
  const resendInvite = async (row) => {
    setResendingId(row.id);
    try {
      const fn = httpsCallable(getFunctions(), "resendUserInvite");
      await fn({ inviteId: row.id });
      setSnack({ severity: "success", message: `Invitation resent to ${row.email}` });
    } catch (err) {
      console.error("resend invite failed", err);
      setSnack({
        severity: "error",
        message: err?.message || "Could not resend invitation",
      });
    }
    setResendingId(null);
  };

  if (!clientId) {
    return (
      <Typography color="text.secondary" sx={{ mt: 4 }}>
        No client is associated with this account.
      </Typography>
    );
  }

  const summaryChips = [
    { label: "Total", value: counts.total, color: "default" },
    { label: "Active", value: counts.active, color: "success" },
    { label: "Pending", value: counts.pending, color: "info" },
    { label: "Expired", value: counts.expired, color: "warning" },
  ];

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Users
        </Typography>
        <Stack direction="row" spacing={1}>
          {summaryChips.map((s) => (
            <Chip
              key={s.label}
              color={s.color}
              variant={s.color === "default" ? "outlined" : "filled"}
              label={`${s.value} ${s.label}`}
              size="small"
            />
          ))}
        </Stack>
        <Box sx={{ flexGrow: 1 }} />
        <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Search name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 240 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Role</InputLabel>
            <Select label="Role" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <MenuItem value="all">All roles</MenuItem>
              {roleOptions.map((r) => (
                <MenuItem key={r} value={r}>
                  {roleLabel(r)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="all">All statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="revoked">Revoked</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setAddOpen(true)}
          >
            Add User
          </Button>
        </Stack>
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "70vh" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  { id: "name", label: "Name" },
                  { id: "email", label: "Email" },
                  { id: "role", label: "Role" },
                  { id: "status", label: "Status", align: "center" },
                  { id: "lastLogin", label: "Last Login" },
                  { id: "lastActive", label: "Last Active" },
                ].map((col, idx) => (
                  <React.Fragment key={col.id}>
                    <TableCell
                      align={col.align || "left"}
                      sortDirection={sortField === col.id ? sortDir : false}
                    >
                      <TableSortLabel
                        active={sortField === col.id}
                        direction={sortField === col.id ? sortDir : "asc"}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                    {/* Non-sortable Properties column sits between Role and Status */}
                    {idx === 2 && <TableCell>Properties</TableCell>}
                  </React.Fragment>
                ))}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No users match.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r) => {
                  const st = STATUS_STYLES[r.status] || STATUS_STYLES.active;
                  const propNames = r.properties.map(
                    (p) => p.displayName || p.name || p.id
                  );
                  return (
                    <TableRow
                      key={r.key}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => setEditRow(r)}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {r.name}
                          </Typography>
                          {r.employeeLinked && (
                            <Tooltip title="Linked to an employee record">
                              <BadgeIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {r.email}
                        </Typography>
                      </TableCell>
                      <TableCell>{roleLabel(r.userType)}</TableCell>
                      <TableCell>
                        {propNames.length === 0 ? (
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        ) : (
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            {propNames.slice(0, 2).map((n, i) => (
                              <Chip key={i} label={n} size="small" variant="outlined" />
                            ))}
                            {propNames.length > 2 && (
                              <Tooltip title={propNames.slice(2).join(", ")}>
                                <Chip label={`+${propNames.length - 2}`} size="small" />
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            r.kind === "invite" && r.invitedAt
                              ? `Invited ${fmtRelative(r.invitedAt)}${
                                  r.invitedBy ? ` by ${r.invitedBy}` : ""
                                }`
                              : ""
                          }
                        >
                          <Chip
                            size="small"
                            label={st.label}
                            sx={{ bgcolor: st.color, color: "#fff", fontWeight: 600 }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {r.kind === "invite" ? (
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        ) : (
                          <Tooltip title={fmtAbsolute(r.lastLoginAt)}>
                            <Typography variant="body2">
                              {fmtRelative(r.lastLoginAt)}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        {r.kind === "invite" ? (
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        ) : (
                          <Tooltip title={fmtAbsolute(r.lastActiveAt)}>
                            <Typography variant="body2">
                              {fmtRelative(r.lastActiveAt)}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {r.kind === "invite" ? (
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Resend invitation email">
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  disabled={resendingId === r.id}
                                  onClick={() => resendInvite(r)}
                                >
                                  <SendIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {r.status === "pending" && (
                              <Tooltip title="Copy registration link">
                                <IconButton size="small" onClick={() => copyInviteLink(r.id)}>
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {r.status !== "revoked" && (
                              <Tooltip title="Revoke invitation">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => revokeInvite(r.id)}
                                >
                                  <BlockIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        Last login is the user's true sign-in time; last activity is updated while the app is open.
        Both begin populating from each user's next session.
      </Typography>

      <UserEditDialog
        open={!!editRow}
        row={editRow}
        clientId={clientId}
        clientProperties={clientProperties}
        onClose={() => setEditRow(null)}
        onSaved={(msg) => setSnack(msg)}
      />

      <AddUserDialog
        open={addOpen}
        clientId={clientId}
        clientProperties={clientProperties}
        onClose={() => setAddOpen(false)}
        onSent={(msg) => setSnack(msg)}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">
            {snack.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
